import {
  metrics,
  type Span,
  SpanStatusCode,
  trace as traceApi,
} from '@opentelemetry/api'
import { flatten } from 'flat'

type TracingOptions = {
  collectInput?: boolean
  collectResult?: boolean
  instrumentedContextFields?: string[]
  headers?: string[]
  serviceName?: string
  metricsEnabled?: boolean
  baseProcedure?: any
}

/**
 *
 * @param options
 * @param options.collectInput - Whether to collect the input of the request. Defaults to false.
 *
 * @returns
 */
export const tracing = (options?: TracingOptions) => {
  const serviceName =
    options?.serviceName ||
    (process.env.OTEL_SERVICE_NAME as string) ||
    'unknown-service-trpc'
  const tracer = traceApi.getTracer(serviceName)
  const meter = metrics.getMeter(serviceName)

  // Counter for procedure calls
  const procedureCounter = meter.createCounter('trpc.procedure.calls', {
    description: 'Count of tRPC procedure calls',
  })

  // Histogram for procedure duration
  const procedureDuration = meter.createHistogram('trpc.procedure.duration', {
    description: 'Duration of tRPC procedure calls in milliseconds',
    unit: 'ms',
  })

  const resolvedOptions = options || {}

  return {
    trace: resolvedOptions.baseProcedure.use((opts) => {
      return opts.next({
        ctx: {
          fromPlugin: tracer.startActiveSpan(
            `TRPC ${opts.type}`,
            async (span: Span) => {
              const startTime = performance.now()
              try {
                const result = await opts.next()

                // opts.rawInput is for v10, `opts.getRawInput` is for v11
                if (
                  resolvedOptions.collectInput &&
                  typeof opts.rawInput === 'object'
                ) {
                  span.setAttributes(flatten({ input: opts.rawInput }))
                }

                if (resolvedOptions.collectResult) {
                  span.setAttributes(flatten({ output: result }))
                }

                const meta = { path: opts.path, type: opts.type, ok: result.ok }
                span.setAttributes(meta)

                // Record metrics
                if (resolvedOptions.metricsEnabled) {
                  const duration = performance.now() - startTime
                  const attributes = {
                    procedure: opts.path,
                    type: opts.type,
                    status: result.ok ? 'success' : 'error',
                  }

                  procedureCounter.add(1, attributes)
                  procedureDuration.record(duration, attributes)
                }

                span.end()
                return result
              } catch (e) {
                const duration = performance.now() - startTime
                if (e instanceof Error) {
                  span.recordException(e)
                  span.setStatus({
                    code: SpanStatusCode.ERROR,
                    message: e.message,
                  })
                } else {
                  span.setStatus({
                    code: SpanStatusCode.ERROR,
                    message: JSON.stringify(e),
                  })
                }

                if (resolvedOptions.metricsEnabled) {
                  const attributes = {
                    procedure: opts.path,
                    type: opts.type,
                    status: 'error',
                  }

                  procedureCounter.add(1, attributes)
                  procedureDuration.record(duration, attributes)
                }

                span.end()
                throw e
              }
            }
          ),
        },
      })
    }),
  }
}
